// src/app/components/common/SimpleAccountSelector.tsx
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Loader2, Library } from 'lucide-react';
import { cn } from '@/lib/utils';
import { listAccountTypes } from '@/app/services/accountTypeService';
import { listAccountGroups } from '@/app/services/accountGroupService';
import { listAccounts } from '@/app/services/accountService';
import { AccountTypeResponse } from '@/app/types/accountType';
import { AccountGroup } from '@/app/types/accountGroup';
import { Account } from '@/app/types/account';
import { toast } from 'sonner';
import { PaginatedResponse } from '@/app/types';

const PAGE_SIZE = 500;

// Helper function to fetch all pages for a given list function
const fetchAllPages = async <T,>(
    listFunction: (params: { skip: number; limit: number }) => Promise<PaginatedResponse<T>>,
    pageSize: number
): Promise<T[]> => {
    let allItems: T[] = [];
    let currentPage = 0;
    let totalItems = 0;
    let fetchedItems = 0;

    do {
        const skip = currentPage * pageSize;
        try {
            const response = await listFunction({ skip, limit: pageSize });
            allItems = allItems.concat(response.items);
            totalItems = response.total;
            fetchedItems += response.items.length;
            currentPage++;
        } catch (error) {
            console.error("Error fetching page:", error);
            throw error;
        }
    } while (fetchedItems < totalItems);

    return allItems;
};

interface SimpleAccountSelectorProps {
  value: number | null | undefined; // Selected Account ID
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  filterAccountNature?: 'debit' | 'credit';
  filterAccountTypes?: string[]; // Array of type *names*
  filterAccountGroups?: number[]; // Array of group *IDs*
  filterAccountIds?: number[]; // Array of specific account *IDs* to show
}

export const SimpleAccountSelector: React.FC<SimpleAccountSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select account...",
  disabled = false,
  filterAccountNature,
  filterAccountTypes,
  filterAccountGroups,
  filterAccountIds,
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  // Determine if filtering requires groups/types
  const needsGroups = !!(filterAccountNature || filterAccountTypes || filterAccountGroups);
  const needsTypes = !!(filterAccountNature || filterAccountTypes);

  // --- Fetching logic ---
  const [allAccountGroups, setAllAccountGroups] = useState<AccountGroup[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  // Initialize fetching state based on whether we *need* the data
  const [isFetchingAllGroups, setIsFetchingAllGroups] = useState(needsGroups);
  const [isFetchingAllAccounts, setIsFetchingAllAccounts] = useState(true); // Always fetch accounts
  const [groupFetchError, setGroupFetchError] = useState<Error | null>(null);
  const [accountFetchError, setAccountFetchError] = useState<Error | null>(null);

  // Fetch Types only if needed
  const { data: accountTypesData, isLoading: isLoadingTypes, isError: isErrorTypes, error: errorTypes } = useQuery<PaginatedResponse<AccountTypeResponse>>({
    queryKey: ['accountTypesSimpleSelector', needsTypes], // Include dependency in key
    queryFn: () => listAccountTypes({ limit: 100 }),
    staleTime: Infinity,
    enabled: needsTypes, // Only run query if types are needed
  });

  // Fetch Groups only if needed
  useEffect(() => {
    if (!needsGroups) {
      setIsFetchingAllGroups(false); // Ensure loading state is false if not needed
      return; // Don't fetch if groups aren't required for filtering
    }
    const fetchGroups = async (): Promise<void> => {
      setIsFetchingAllGroups(true);
      setGroupFetchError(null);
      try {
        const groups = await fetchAllPages(listAccountGroups, PAGE_SIZE);
        setAllAccountGroups(groups);
      } catch (error) {
        console.error("Error fetching all account groups:", error);
        setGroupFetchError(error as Error);
        toast.error(`Error loading all account groups: ${(error as Error)?.message}`);
      } finally {
        setIsFetchingAllGroups(false);
      }
    };
    fetchGroups();
  }, [queryClient, needsGroups]); // Add needsGroups dependency

  // Fetch all accounts (always needed)
  useEffect(() => {
    const fetchAccounts = async (): Promise<void> => {
      setIsFetchingAllAccounts(true);
      setAccountFetchError(null);
      try {
        const accounts = await fetchAllPages(listAccounts, PAGE_SIZE);
        setAllAccounts(accounts);
      } catch (error) {
        console.error("Error fetching all accounts:", error);
        setAccountFetchError(error as Error);
        toast.error(`Error loading all accounts: ${(error as Error)?.message}`);
      } finally {
        setIsFetchingAllAccounts(false);
      }
    };
    fetchAccounts();
  }, [queryClient]);

  // Memoize fetched data, provide empty arrays if not fetched/needed
  const accountTypes = useMemo(() => (needsTypes ? accountTypesData?.items ?? [] : []), [accountTypesData, needsTypes]);
  const accountGroups = useMemo(() => (needsGroups ? allAccountGroups : []), [allAccountGroups, needsGroups]);
  const accounts = useMemo(() => allAccounts, [allAccounts]);

  // --- Filter and Sort Accounts ---
  const filteredAndSortedAccounts = useMemo(() => {
    // Base check: wait for accounts to load
    if (isFetchingAllAccounts || !accounts.length) {
        return [];
    }

    let filtered = accounts;

    // First apply account ID filter if specified
    if (filterAccountIds && filterAccountIds.length > 0) {
        filtered = filtered.filter(acc => filterAccountIds.includes(acc.id));
    }

    // Apply other filters only if needed and data is ready
    if (needsGroups) {
        // Wait for groups if needed
        if (isFetchingAllGroups || !accountGroups.length) return [];
        // Wait for types if needed
        if (needsTypes && (isLoadingTypes || !accountTypes.length)) return [];

        const typeNameToIdMap = new Map(accountTypes.map(t => [t.name.toLowerCase(), t.id]));
        const filterTypeIds = needsTypes && filterAccountTypes
          ? filterAccountTypes.map(name => typeNameToIdMap.get(name.toLowerCase())).filter((id): id is number => id !== undefined)
          : null;

        filtered = filtered.filter(acc => {
          const group = accountGroups.find(g => g.id === acc.account_group_id);
          if (!group) return false;

          // Check type only if needed for filtering
          if (needsTypes) {
            const accountType = accountTypes.find(t => t.id === group.account_type_id);
            if (!accountType) return false;
            if (filterAccountNature && accountType.nature !== filterAccountNature) {
                return false;
            }
            if (filterTypeIds && !filterTypeIds.includes(accountType.id)) {
                return false;
            }
          }

          // Check group filter
          if (filterAccountGroups && !filterAccountGroups.includes(group.id)) {
            return false;
          }
          return true;
        });
    }

    // Sort the (potentially filtered) list alphabetically by name
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));

  }, [
      accounts, accountGroups, accountTypes, 
      filterAccountNature, filterAccountTypes, filterAccountGroups, filterAccountIds,
      isLoadingTypes, isFetchingAllGroups, isFetchingAllAccounts, 
      needsGroups, needsTypes
  ]); // Update dependency array

  // --- Find selected label ---
  const selectedLabel = useMemo(() => {
    if (value === null || value === undefined) return placeholder;
    const selectedAccount = accounts.find(acc => acc.id === value);
    return selectedAccount ? selectedAccount.name : placeholder;
  }, [value, accounts, placeholder]);

  // --- Simplified handleSelect ---
  const handleSelect = useCallback((selectedAccountName: string) => {
    const selectedAccount = filteredAndSortedAccounts.find(acc => acc.name === selectedAccountName);
    if (selectedAccount) {
      onChange(selectedAccount.id);
      setOpen(false);
      triggerRef.current?.focus();
    } else {
        console.warn("Selected account name not found in filtered list:", selectedAccountName);
    }
  }, [filteredAndSortedAccounts, onChange]);


  // --- Focus input on open ---
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearchQuery(''); // Clear search on close
    }
  }, [open]);

  // --- Error handling ---
  useEffect(() => {
    // Conditionally check type errors
    if (needsTypes && isErrorTypes) toast.error(`Error loading account types: ${(errorTypes as Error)?.message}`);
    // Group and Account errors are handled in their fetch useEffects (which are also conditional for groups)
  }, [isErrorTypes, errorTypes, needsTypes]); // Add needsTypes dependency

  // Recalculate overall loading/error state based on needed data
  const isLoading = isFetchingAllAccounts || 
                    (needsGroups && isFetchingAllGroups) || 
                    (needsTypes && isLoadingTypes);
  const isError = accountFetchError || 
                  (needsGroups && groupFetchError) || 
                  (needsTypes && isErrorTypes);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || isLoading || !!isError} // Check isError directly
        >
          {isLoading ? (
            <span className="flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...
            </span>
          ) : isError ? (
            <span className="text-destructive">Error loading data</span>
          ) : (
            <span className="truncate">
              {selectedLabel}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0"
      >
        <Command> {/* Use default filtering */}
          <CommandInput
            ref={inputRef}
            placeholder="Search accounts..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            disabled={isLoading || !!isError} // Check isError directly
          />
          <CommandList>
            {isLoading && <CommandEmpty>Loading accounts...</CommandEmpty>}
            {isError && !isLoading && <CommandEmpty>Error loading accounts.</CommandEmpty>}
            {!isLoading && !isError && filteredAndSortedAccounts.length === 0 && (
              <CommandEmpty>No accounts found.</CommandEmpty>
            )}
            {!isLoading && !isError && filteredAndSortedAccounts.length > 0 && (
              <CommandGroup>
                {filteredAndSortedAccounts.map((account) => (
                  <CommandItem
                    key={account.id}
                    value={account.name} // Use name for Command filtering/selection value
                    onSelect={handleSelect} // Pass name to handler
                    className={cn(
                      "flex items-center cursor-pointer",
                      account.id === value ? "font-medium" : ""
                    )}
                  >
                    <Library className="mr-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate flex-grow">{account.name}</span>
                    {account.id === value && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}; 